import { useState, useEffect } from "react"
import { useShallow } from "zustand/react/shallow"
import { WITHDRAWN_REASONS } from "../../../../schemas/dataSchemas/enum/enum"

// components
import CellActions from "../CellActions/CellActions"
import RowActions from "../../../components/RowActions/RowActions"
import Label from "components/Label/Label"
import CellValidation from "../../../components/CellValidation/CellValidation"
import { Select, TextInput } from "components-design-system"
import CharCounter from "components/CharCounter/CharCounter"

// context
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"

import "./CellHeader.scss"

const CellHeader = ({
  row,
  rowIndex,
  period,
  type,
  participantData,
  setParticipantData,
  errorKey,
  hideComment,
  schema,
}) => {
  const [showActions, setShowActions] = useState(false)
  const [isTitleOther, setIsTitleOther] = useState(false)

  const { participantRegistries, participantView, tableView } = useResultsTableViewStore()

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      readOnly: state.readOnly,
    }))
  )

  useEffect(() => {
    if (row?.required && isTitleOther === true) setIsTitleOther(false)
    else if (row?.required === false && isTitleOther === false) setIsTitleOther(true)
  }, [row.required])

  const headerStyle = {
    width: 250 - 24,
    minWidth: 250 - 24,
  }

  let rows = period[type]

  const canDelete = () => {
    if (type === "milestone_list" && (rowIndex === 0 || rowIndex === rows.length - 1)) return false
    return true
  }

  const canShowDown = () => {
    if (type === "milestone_list" && rows.length > 3 && rowIndex < rows.length - 2 && rowIndex !== 0) return true
    else if (type === "drop_withdrawn_reason_list" && rowIndex < rows.length - 1) return true
    return false
  }

  const canShowUp = () => {
    if (type === "milestone_list" && rows.length > 3 && rowIndex !== 1 && rowIndex < rows.length - 1 && rowIndex !== 0)
      return true
    else if (type === "drop_withdrawn_reason_list" && rowIndex !== 0) return true
    return false
  }

  const handleDelete = (applyAll) => {
    let tempData = { ...participantData }

    participantRegistries.forEach((registry) => {
      if (applyAll || participantView === registry) {
        let foundPeriodIndex = tempData[registry].findIndex((item) => item?.id === period.id)
        if (foundPeriodIndex !== -1) {
          let rowIndex = tempData[registry][foundPeriodIndex][type].findIndex((item) => item?.id === row.id)
          if (
            (rowIndex !== -1 &&
              rowIndex !== 0 &&
              rowIndex !== tempData?.[registry]?.[foundPeriodIndex]?.[type]?.length - 1) ||
            type === "drop_withdrawn_reason_list"
          ) {
            tempData[registry][foundPeriodIndex][type].splice(rowIndex, 1)
          }
        }
      }
    })

    setParticipantData(tempData)
  }

  const handleUp = () => {
    let tempData = { ...participantData }
    participantRegistries.forEach((registry) => {
      let foundPeriodIndex = tempData[registry].findIndex((item) => item?.id === period.id)
      if (foundPeriodIndex !== -1) {
        let rowIndex = tempData[registry][foundPeriodIndex][type].findIndex((item) => item?.id === row.id)
        if ((rowIndex !== -1 && rowIndex !== 1) || (type === "drop_withdrawn_reason_list" && rowIndex !== 0)) {
          let savedPeriod = tempData[registry][foundPeriodIndex][type][rowIndex - 1]
          tempData[registry][foundPeriodIndex][type][rowIndex - 1] =
            tempData[registry][foundPeriodIndex][type][rowIndex]
          tempData[registry][foundPeriodIndex][type][rowIndex] = savedPeriod
        }
      }
    })
    setParticipantData(tempData)
  }

  const handleDown = () => {
    let tempData = { ...participantData }
    participantRegistries.forEach((registry) => {
      let foundPeriodIndex = tempData[registry].findIndex((item) => item?.id === period.id)
      if (foundPeriodIndex !== -1) {
        let rowIndex = tempData[registry][foundPeriodIndex][type].findIndex((item) => item?.id === row.id)

        if (
          (rowIndex !== -1 && rowIndex !== tempData[registry][foundPeriodIndex][type].length - 2) ||
          (type === "drop_withdrawn_reason_list" && rowIndex !== tempData[registry][foundPeriodIndex][type].length - 1)
        ) {
          let savedPeriod = tempData[registry][foundPeriodIndex][type][rowIndex + 1]
          tempData[registry][foundPeriodIndex][type][rowIndex + 1] =
            tempData[registry][foundPeriodIndex][type][rowIndex]
          tempData[registry][foundPeriodIndex][type][rowIndex] = savedPeriod
        }
      }
    })
    setParticipantData(tempData)
  }

  const onChange = (e) => {
    let tempData = { ...participantData }
    participantRegistries.forEach((registry) => {
      let foundPeriodIndex = tempData[registry].findIndex((item) => item?.id === period.id)
      if (foundPeriodIndex !== -1) {
        let foundMilestoneIndex = tempData[registry][foundPeriodIndex][type].findIndex((item) => item?.id === row.id)
        if (foundMilestoneIndex !== -1) {
          if (type === "drop_withdrawn_reason_list") {
            if (isTitleOther) {
              tempData[registry][foundPeriodIndex][type][foundMilestoneIndex].title = e
            } else {
              const foundOption = WITHDRAWN_REASONS.find(
                (option) => option.value === e && option.registries[registry.split("_")[0]]
              )
              if (foundOption?.value === "Other") {
                tempData[registry][foundPeriodIndex][type][foundMilestoneIndex].title = ""
                tempData[registry][foundPeriodIndex][type][foundMilestoneIndex].required = false
              } else {
                tempData[registry][foundPeriodIndex][type][foundMilestoneIndex].title = e
              }
            }
          } else {
            tempData[registry][foundPeriodIndex][type][foundMilestoneIndex].title = e
          }
        }
      }
    })
    setParticipantData(tempData)
  }

  const renderTitle = () => {
    if (type === "milestone_list" && !row.required) {
      return (
        <TextInput
          placeholder={"Title"}
          readOnly={readOnly}
          value={row.title || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    } else if (type === "drop_withdrawn_reason_list") {
      let value = row.title
      if (readOnly) {
        const foundOption = WITHDRAWN_REASONS.find(
          (option) => option.value === row.title && option.registries[tableView] && row.required !== false
        )
        if (foundOption) {
          value = foundOption.registries[tableView]
        }
      }

      if (isTitleOther) {
        return (
          <TextInput
            placeholder={"Title"}
            readOnly={readOnly}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      } else {
        return (
          <Select
            readOnly={readOnly}
            options={WITHDRAWN_REASONS.filter((option) => {
              if (tableView === "ctg" && option.registries["global"]) return true
              return option.registries[tableView]
            }).map((option) => ({
              value: option.value,
              label: option.registries[tableView],
              disabled: period.drop_withdrawn_reason_list.find(
                (reason) => reason.title === option.value && reason.required !== false
              ),
            }))}
            value={value}
            allowClear={false}
            onChange={onChange}
            className="rjs-full"
          />
        )
      }
    }

    return row.title
  }

  const saveComment = (value) => {
    let tempData = { ...participantData }
    participantRegistries.forEach((registry) => {
      let foundPeriodIndex = tempData[registry].findIndex((item) => item?.id === period.id)
      if (foundPeriodIndex !== -1) {
        let foundMilestoneIndex = tempData[registry][foundPeriodIndex][type].findIndex((item) => item?.id === row.id)
        if (foundMilestoneIndex !== -1) {
          tempData[registry][foundPeriodIndex][type][foundMilestoneIndex].comment = value
        }
      }
    })
    setParticipantData(tempData)
  }

  const renderCharCounter = () => {
    if (type === "milestone_list" && !row.required && schema?.title?.charCountEnabled) {
      return (
        <CharCounter
          value={row.title || ""}
          maxChars={schema?.title?.maxChars}
        />
      )
    } else if (type === "drop_withdrawn_reason_list" && schema?.other?.charCountEnabled) {
      if (isTitleOther) {
        return (
          <CharCounter
            value={row.title || ""}
            maxChars={schema?.other?.maxChars}
          />
        )
      }
    }
  }

  return (
    <td
      className="pf-cell-header-container "
      style={headerStyle}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex items-center">{renderTitle()}</div>
      <div className="header-helper-container">
        <div className="left-helper-container">
          <CellActions
            showActions={showActions}
            setShowActions={setShowActions}
            comment={row.comment}
            saveComment={saveComment}
            jpath={`pf-${period.id}-${type}-header-${row.id}`}
            type={type}
            hideComment={hideComment}
          />
          <RowActions
            showActions={showActions}
            setShowActions={setShowActions}
            showDown={canShowDown()}
            showUp={canShowUp()}
            handleUp={handleUp}
            handleDown={handleDown}
            handleDelete={handleDelete}
            canDelete={canDelete()}
            hideComment={true}
            jpath={`ParticipantFlowGroup.pf-${period.id}-${type}-header-${row.id}`}
            deleteModalTitle="Delete row?"
          />
        </div>
        <div className="right-helper-container">{formView.id !== "global" && !readOnly && renderCharCounter()}</div>
      </div>
      <div className="prs-comment-container">
        {row.comment && tableView !== "eudract" && <Label style={{ marginTop: 6 }}>{row.comment}</Label>}
      </div>
      <CellValidation
        table="participants"
        errorKey={errorKey}
      />
    </td>
  )
}

export default CellHeader
